import React from 'react';

interface ReferenceRangeIndicatorProps {
  source: 'original' | 'enhanced' | 'none' | 'standard' | 'lab';
  isLoading?: boolean;
  onRequestEnhancement?: () => void;
  sourceDetails?: {
    source?: string;
    notes?: string;
  };
}

export const ReferenceRangeIndicator: React.FC<ReferenceRangeIndicatorProps> = ({
  source,
  isLoading,
  onRequestEnhancement,
  sourceDetails
}) => {
  if (isLoading) {
    return (
      <div className="inline-flex items-center gap-1.5 text-xs text-gray-500">
        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
        <span>Fetching reference range...</span>
      </div>
    );
  }

  switch (source) {
    case 'original':
      return null; // Don't show anything for original reference ranges
      
    case 'enhanced':
      return (
        <div className="group relative inline-flex items-center gap-1.5 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-full text-xs text-blue-700 cursor-help ai-researched-badge">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span className="font-medium">AI-researched range</span>
          <div className="absolute bottom-full left-0 mb-2 w-80 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <div className="font-semibold mb-1">AI-Generated Reference Range</div>
                <div className="text-gray-300 text-xs leading-relaxed mb-2">
                  This reference range was automatically researched using AI from standard medical sources because your original lab report didn't include one.
                </div>
                {sourceDetails?.source && (
                  <div className="text-blue-300 text-xs font-medium">
                    Source: {sourceDetails.source}
                  </div>
                )}
                {sourceDetails?.notes && (
                  <div className="text-gray-400 text-xs mt-1">
                    Note: {sourceDetails.notes}
                  </div>
                )}
                <div className="text-yellow-300 text-xs mt-2 font-medium">
                  ⚠️ Always consult with your healthcare provider for interpretation.
                </div>
              </div>
            </div>
            <div className="absolute bottom-0 left-8 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"></div>
          </div>
        </div>
      );
      
    case 'none':
      // No longer show button - ranges are fetched automatically
      return null;
      
    case 'standard':
    case 'lab':
      // Don't show indicator for standard lab ranges
      return null;
      
    default:
      return null;
  }
};

export default ReferenceRangeIndicator;